import * as React from 'react';
import type { DraggableAttributes } from '@dnd-kit/core';
import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { CSS } from '@dnd-kit/utilities';

export interface SortableItemRenderProps {
    setNodeRef: (element: HTMLElement | null) => void;
    style: React.CSSProperties;
    isDragging: boolean;
    attributes: DraggableAttributes;
    listeners: SyntheticListenerMap | undefined;
}

export interface SortableItemProps {
    id: UniqueIdentifier;
    disabled?: boolean;
    /**
     * Render the sortable row; put `setNodeRef` on the outermost element of the item.
     * Apply `listeners` + `attributes` only on the drag handle (or whole row) as needed.
     */
    children: (props: SortableItemRenderProps) => React.ReactNode;
}

/**
 * Wrapper for `@dnd-kit/sortable` `useSortable`. Use inside `SortableContext` + `DndContext`.
 * Prefer this over ad-hoc `useSortable` in content-types so drag behavior stays consistent.
 */
export function SortableItem({ id, disabled = false, children }: SortableItemProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
        disabled,
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <>
            {children({
                setNodeRef,
                style,
                isDragging,
                attributes,
                listeners,
            })}
        </>
    );
}

SortableItem.displayName = 'SortableItem';
